import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFirebaseInstances } from '../../../config/firebase/firebase';
import { notifyNewUserSignup } from '../../../utils/webhookService';
import { updateUserField } from '../../../services/userSettingsService';
import { USER_TYPES, USE_CASES, REFERRAL_SOURCES, COUNTRIES } from '../../../constants/signupOptions';
import LegalNoticeModal from '../LegalNoticeModal';
import styles from '../LoginEnhanced.module.css';

const phonePattern = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/;

const ProfileCompletion = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: '',
    company: '',
    jobTitle: '',
    userType: '',
    useCase: '',
    referralSource: '',
    country: '',
    phoneNumber: '',
    termsAccepted: false,
    privacyAccepted: false,
    marketingConsent: false
  });
  const [loading, setLoading] = useState(false);
  const [openNotice, setOpenNotice] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [error, setError] = useState({
    fullName: '',
    company: '',
    jobTitle: '',
    userType: '',
    useCase: '',
    referralSource: '',
    country: '',
    phoneNumber: '',
    termsAccepted: '',
    general: ''
  });

  const openLegalNotice = (notice) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    setOpenNotice(notice);
  };

  useEffect(() => {
    // Get current user and pre-fill display name if available
    const loadUser = async () => {
      const { auth } = await getFirebaseInstances();
      const user = auth.currentUser;

      if (!user) {
        // No user logged in, redirect to login
        navigate('/login');
        return;
      }

      setCurrentUser(user);

      // Pre-fill name if available from OAuth provider
      if (user.displayName) {
        setFormData((prev) => ({ ...prev, fullName: user.displayName }));
      }
    };

    loadUser();
  }, [navigate]);

  // Validation functions
  const validateFullName = (fullName) => {
    if (!fullName || fullName.trim() === '') return 'Full name is required';
    if (fullName.trim().length < 2) return 'Please enter your full name';
    return '';
  };

  const validateCompany = (company) => {
    if (!company || company.trim() === '') return 'Company/Organization is required';
    return '';
  };

  const validateJobTitle = (jobTitle) => {
    if (!jobTitle || jobTitle.trim() === '') return 'Job title is required';
    return '';
  };

  const validateUserType = (userType) => {
    if (!userType) return 'Please select a user type';
    return '';
  };

  const validateUseCase = (useCase) => {
    if (!useCase) return 'Please select your primary use case';
    return '';
  };

  const validateReferralSource = (referralSource) => {
    if (!referralSource) return 'Please tell us how you heard about VerdeKai';
    return '';
  };

  const validateCountry = (country) => {
    if (!country) return 'Please select your country';
    return '';
  };

  const validatePhoneNumber = (phoneNumber) => {
    if (!phoneNumber || phoneNumber.trim() === '') return '';
    if (!phonePattern.test(phoneNumber)) return 'Please enter a valid phone number';
    return '';
  };

  const validateTermsAccepted = (termsAccepted) => {
    if (!termsAccepted) return 'You must accept the Terms of Service';
    return '';
  };

  const validatePrivacyAccepted = (privacyAccepted) => {
    if (!privacyAccepted) return 'You must accept the Privacy Policy';
    return '';
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    setFormData((prev) => ({ ...prev, [name]: newValue }));
    setError((prev) => ({ ...prev, [name]: '', general: '' }));
  };

  const handleBlur = (e) => {
    const { name, value, type, checked } = e.target;
    let err = '';
    const fieldValue = type === 'checkbox' ? checked : value;

    if (name === 'fullName') err = validateFullName(fieldValue);
    if (name === 'company') err = validateCompany(fieldValue);
    if (name === 'jobTitle') err = validateJobTitle(fieldValue);
    if (name === 'userType') err = validateUserType(fieldValue);
    if (name === 'useCase') err = validateUseCase(fieldValue);
    if (name === 'referralSource') err = validateReferralSource(fieldValue);
    if (name === 'country') err = validateCountry(fieldValue);
    if (name === 'phoneNumber') err = validatePhoneNumber(fieldValue);
    if (name === 'termsAccepted') err = validateTermsAccepted(fieldValue);
    if (name === 'privacyAccepted') err = validatePrivacyAccepted(fieldValue);

    setError((prev) => ({ ...prev, [name]: err }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!currentUser) {
      setError((prev) => ({ ...prev, general: 'User session expired. Please log in again.' }));
      return;
    }

    // Validate all fields
    const fullNameErr = validateFullName(formData.fullName);
    const companyErr = validateCompany(formData.company);
    const jobTitleErr = validateJobTitle(formData.jobTitle);
    const userTypeErr = validateUserType(formData.userType);
    const useCaseErr = validateUseCase(formData.useCase);
    const referralSourceErr = validateReferralSource(formData.referralSource);
    const countryErr = validateCountry(formData.country);
    const phoneNumberErr = validatePhoneNumber(formData.phoneNumber);
    const termsAcceptedErr = validateTermsAccepted(formData.termsAccepted);
    const privacyAcceptedErr = validatePrivacyAccepted(formData.privacyAccepted);

    if (
      fullNameErr ||
      companyErr ||
      jobTitleErr ||
      userTypeErr ||
      useCaseErr ||
      referralSourceErr ||
      countryErr ||
      phoneNumberErr ||
      termsAcceptedErr ||
      privacyAcceptedErr
    ) {
      setError({
        fullName: fullNameErr,
        company: companyErr,
        jobTitle: jobTitleErr,
        userType: userTypeErr,
        useCase: useCaseErr,
        referralSource: referralSourceErr,
        country: countryErr,
        phoneNumber: phoneNumberErr,
        termsAccepted: termsAcceptedErr,
        privacyAccepted: privacyAcceptedErr,
        general: 'Please fix the errors above'
      });
      return;
    }

    setLoading(true);
    setError({});

    try {
      // Determine signup method from provider data
      const providerId = currentUser.providerData[0]?.providerId || 'unknown';
      const signupMethod = providerId.includes('google') ? 'google' : providerId.includes('apple') ? 'apple' : 'oauth';

      // Prepare profile data
      const profileData = {
        displayName: formData.fullName,
        fullName: formData.fullName,
        company: formData.company,
        jobTitle: formData.jobTitle,
        userType: formData.userType,
        useCase: formData.useCase,
        referralSource: formData.referralSource,
        country: formData.country,
        phoneNumber: formData.phoneNumber,
        marketingConsent: formData.marketingConsent,
        termsAccepted: formData.termsAccepted,
        privacyAccepted: formData.privacyAccepted,
        profileCompleted: true
      };

      // Update Firestore with all profile data
      const updatePromises = [
        updateUserField(currentUser.uid, 'displayName', profileData.displayName),
        updateUserField(currentUser.uid, 'fullName', profileData.fullName),
        updateUserField(currentUser.uid, 'company', profileData.company),
        updateUserField(currentUser.uid, 'jobTitle', profileData.jobTitle),
        updateUserField(currentUser.uid, 'userType', profileData.userType),
        updateUserField(currentUser.uid, 'useCase', profileData.useCase),
        updateUserField(currentUser.uid, 'referralSource', profileData.referralSource),
        updateUserField(currentUser.uid, 'country', profileData.country),
        updateUserField(currentUser.uid, 'phoneNumber', profileData.phoneNumber),
        updateUserField(currentUser.uid, 'marketingConsent', profileData.marketingConsent),
        updateUserField(currentUser.uid, 'termsAccepted', profileData.termsAccepted),
        updateUserField(currentUser.uid, 'privacyAccepted', profileData.privacyAccepted),
        updateUserField(currentUser.uid, 'profileCompleted', true)
      ];

      await Promise.all(updatePromises);

      console.log(' Profile completed successfully');

      // Send webhook notification with complete profile data
      await notifyNewUserSignup(currentUser, signupMethod, profileData);

      // Redirect to terminal
      navigate('/terminal');
    } catch (err) {
      console.error('Profile completion error:', err);
      setError((prev) => ({ ...prev, general: 'Failed to save profile. Please try again.' }));
    } finally {
      setLoading(false);
    }
  };

  if (!currentUser) {
    return (
      <div className={styles.splitLayout}>
        <div className={styles.rightPanel}>
          <div className={styles.formCard}>
            <p className={styles.loadingText}>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.splitLayout}>
      {/* Left Panel: Branding */}
      <div className={styles.leftPanel}>
        <img src={`${process.env.PUBLIC_URL}/VerdeKai_Logo.jpg`} alt="VerdeKai Legal Terminal" className={styles.brandImage} />
        <div className={styles.brandContent}>
          <h1 className={styles.platformName}>VerdeKai Legal Terminal</h1>
          <h2 className={styles.welcome}>Complete Your Profile</h2>
          <p className={styles.trustMsg}>Just a few more details to get you started</p>
        </div>
      </div>

      {/* Right Panel: Profile Completion Form */}
      <div className={styles.rightPanel}>
        <form className={styles.formCard} onSubmit={handleSubmit} autoComplete="on" noValidate>
          <h2 className={styles.formTitle}>Complete Your Profile</h2>
          <p className={styles.formSubtitle}>Tell us more about yourself</p>

          {error.general && <div className={styles.errorMsg}>{error.general}</div>}

          {/* Personal Information */}
          <div className={styles.formGroup}>
            <div className={styles.floatingLabelGroup}>
              <input
                type="text"
                name="fullName"
                id="fullName"
                autoComplete="name"
                value={formData.fullName}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className={error.fullName ? styles.inputError : ''}
                required
                disabled={loading}
              />
              <label htmlFor="fullName" className={formData.fullName ? styles.hasValue : ''}>Full Name *</label>
            </div>
            {error.fullName && <span className={styles.errorText}>{error.fullName}</span>}
          </div>

          <div className={styles.formGroup}>
            <div className={styles.floatingLabelGroup}>
              <input
                type="tel"
                name="phoneNumber"
                id="phoneNumber"
                autoComplete="tel"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className={error.phoneNumber ? styles.inputError : ''}
                disabled={loading}
              />
              <label htmlFor="phoneNumber" className={formData.phoneNumber ? styles.hasValue : ''}>Phone Number (Optional)</label>
            </div>
            {error.phoneNumber && <span className={styles.errorText}>{error.phoneNumber}</span>}
          </div>

          {/* Professional Information */}
          <div className={styles.formGroup}>
            <div className={styles.floatingLabelGroup}>
              <input
                type="text"
                name="company"
                id="company"
                autoComplete="organization"
                value={formData.company}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className={error.company ? styles.inputError : ''}
                required
                disabled={loading}
              />
              <label htmlFor="company" className={formData.company ? styles.hasValue : ''}>Company/Organization *</label>
            </div>
            {error.company && <span className={styles.errorText}>{error.company}</span>}
          </div>

          <div className={styles.formGroup}>
            <div className={styles.floatingLabelGroup}>
              <input
                type="text"
                name="jobTitle"
                id="jobTitle"
                autoComplete="organization-title"
                value={formData.jobTitle}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className={error.jobTitle ? styles.inputError : ''}
                required
                disabled={loading}
              />
              <label htmlFor="jobTitle" className={formData.jobTitle ? styles.hasValue : ''}>Job Title *</label>
            </div>
            {error.jobTitle && <span className={styles.errorText}>{error.jobTitle}</span>}
          </div>

          {/* User Type - Radio Buttons */}
          <div className={styles.formGroup}>
            <label className={styles.fieldLabel}>
              I am a(n): *
            </label>
            <div className={styles.radioRow}>
              {USER_TYPES.map((type) => (
                <label key={type.value} className={styles.radioOption}>
                  <input
                    type="radio"
                    name="userType"
                    value={type.value}
                    checked={formData.userType === type.value}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    disabled={loading}
                  />
                  <span>{type.label}</span>
                </label>
              ))}
            </div>
            {error.userType && <span className={styles.errorText}>{error.userType}</span>}
          </div>

          {/* Dropdowns */}
          <div className={styles.formGroup}>
            <select
              name="useCase"
              id="useCase"
              value={formData.useCase}
              onChange={handleInputChange}
              onBlur={handleBlur}
              className={`${error.useCase ? styles.inputError : ''} ${styles.formSelect}`}
              required
              disabled={loading}
            >
              <option value="">Select Your Primary Use Case *</option>
              {USE_CASES.map((useCase) => (
                <option key={useCase.value} value={useCase.value}>
                  {useCase.label}
                </option>
              ))}
            </select>
            {error.useCase && <span className={styles.errorText}>{error.useCase}</span>}
          </div>

          <div className={styles.formGroup}>
            <select
              name="referralSource"
              id="referralSource"
              value={formData.referralSource}
              onChange={handleInputChange}
              onBlur={handleBlur}
              className={`${error.referralSource ? styles.inputError : ''} ${styles.formSelect}`}
              required
              disabled={loading}
            >
              <option value="">How did you hear about us? *</option>
              {REFERRAL_SOURCES.map((source) => (
                <option key={source.value} value={source.value}>
                  {source.label}
                </option>
              ))}
            </select>
            {error.referralSource && <span className={styles.errorText}>{error.referralSource}</span>}
          </div>

          <div className={styles.formGroup}>
            <select
              name="country"
              id="country"
              value={formData.country}
              onChange={handleInputChange}
              onBlur={handleBlur}
              className={`${error.country ? styles.inputError : ''} ${styles.formSelect}`}
              required
              disabled={loading}
            >
              <option value="">Select Your Country *</option>
              {COUNTRIES.map((country) => (
                <option key={country.value} value={country.value}>
                  {country.label}
                </option>
              ))}
            </select>
            {error.country && <span className={styles.errorText}>{error.country}</span>}
          </div>

          {/* Privacy & Compliance */}
          <div className={styles.formGroup} style={{ marginTop: '1.5rem', marginBottom: '1rem' }}>
            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                name="termsAccepted"
                checked={formData.termsAccepted}
                onChange={handleInputChange}
                onBlur={handleBlur}
                disabled={loading}
              />
              <span className={styles.checkboxLabel}>
                I accept the{' '}
                <button type="button" className={styles.legalNoticeLink} onClick={openLegalNotice('terms')}>
                  Terms of Service
                </button>
                {' '}*
              </span>
            </label>
            {error.termsAccepted && <span className={styles.errorText}>{error.termsAccepted}</span>}
          </div>

          <div className={styles.formGroup} style={{ marginBottom: '1rem' }}>
            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                name="privacyAccepted"
                checked={formData.privacyAccepted}
                onChange={handleInputChange}
                onBlur={handleBlur}
                disabled={loading}
              />
              <span className={styles.checkboxLabel}>
                I acknowledge the{' '}
                <button type="button" className={styles.legalNoticeLink} onClick={openLegalNotice('privacy')}>
                  Privacy Policy
                </button>
                {' '}*
              </span>
            </label>
            {error.privacyAccepted && <span className={styles.errorText}>{error.privacyAccepted}</span>}
          </div>

          <div className={styles.formGroup} style={{ marginBottom: '1.5rem' }}>
            <label className={styles.checkboxRow} style={{ marginBottom: 0 }}>
              <input
                type="checkbox"
                name="marketingConsent"
                checked={formData.marketingConsent}
                onChange={handleInputChange}
                disabled={loading}
              />
              <span className={styles.checkboxLabel}>
                I would like to receive updates, news, and promotional content from VerdeKai (Optional)
              </span>
            </label>
          </div>

          <button type="submit" className={styles.signInBtn} disabled={loading}>
            {loading ? 'Completing Profile...' : 'Complete Profile & Continue'}
          </button>
        </form>
      </div>
      <LegalNoticeModal
        isOpen={openNotice !== null}
        title={openNotice === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}
        onClose={() => setOpenNotice(null)}
      />
    </div>
  );
};

export default ProfileCompletion;
